import { useEffect, useMemo, useRef, useState } from "react";
import {
    GetAllCustomer,
    GetCustomerById,
    AddCustomer,
    UpdateCustomer,
} from "../services/CustomerService";
import { GetAllProvince } from "../services/ProvinceService";
import { GetAllCity } from "../services/CityService.js";
import { useLanguage } from "./LanguageContext"; // مسیر واقعی خودت

/* -------------------------------------------------------------------------- */
/*  فونت پایه                                                                  */
/* -------------------------------------------------------------------------- */
const FONT = "IRANSansWeb, Vazirmatn, sans-serif";

/* -------------------------------------------------------------------------- */
/*  متن‌ها                                                                     */
/* -------------------------------------------------------------------------- */
const TEXTS = {
    fa: {
        dir: "rtl",
        title: "مدیریت مشتریان",
        subtitle: "لیست مشتریان را مشاهده و مدیریت کنید",
        searchPlaceholder: "جستجو با نام، فروشگاه، موبایل یا کد مشتری...",
        addNew: "افزودن مشتری",
        edit: "ویرایش",
        code: "کد مشتری",
        fullName: "نام و نام خانوادگی",
        storeName: "نام فروشگاه",
        mobile: "شماره موبایل",
        groups: "گروه‌بندی‌های عضو",
        province: "استان",
        city: "شهر",
        address: "آدرس",
        status: "وضعیت",
        active: "فعال",
        inactive: "غیرفعال",
        filterAll: "همه",
        save: "ذخیره",
        cancel: "انصراف",
        modalAddTitle: "افزودن مشتری جدید",
        modalEditTitle: "ویرایش مشتری",
        loading: "در حال بارگذاری...",
        saving: "در حال ذخیره...",
        empty: "هنوز هیچ مشتری‌ای ثبت نشده است",
        emptyAction: "افزودن اولین مشتری",
        noSearchResult: "نتیجه‌ای یافت نشد",
        toastFullNameRequired: "نام و نام خانوادگی را وارد کنید",
        toastMobileRequired: "شماره موبایل را وارد کنید",
        toastAddSuccess: "مشتری با موفقیت افزوده شد",
        toastAddFail: "خطا در افزودن مشتری",
        toastUpdateSuccess: "مشتری با موفقیت ویرایش شد",
        toastUpdateFail: "خطا در ویرایش مشتری",
        toastFetchFail: "خطا در دریافت لیست مشتریان",
        toastLocationsFetchFail: "خطا در دریافت لیست استان‌ها/شهرها",
        toastMobileDuplicate: "این شماره موبایل قبلاً برای مشتری دیگری ثبت شده است",
        toastStatusSuccess: "وضعیت مشتری تغییر کرد",
        toastStatusFail: "خطا در تغییر وضعیت مشتری",
        toggleStatusTitle: "برای تغییر وضعیت کلیک کنید",
        confirmToggleTitle: "تغییر وضعیت مشتری",
        confirmToggleActivateMessage: (name) => `آیا از فعال کردن مشتری «${name}» مطمئن هستید؟`,
        confirmToggleDeactivateMessage: (name) => `آیا از غیرفعال کردن مشتری «${name}» مطمئن هستید؟`,
        confirmYes: "بله، انجام بده",
        itemsCount: (n) => `${n} مشتری`,
        codeBadge: (code) => `${code}`,
        autoCodeHint: "کد مشتری پس از ذخیره به‌صورت خودکار تخصیص داده می‌شود",
        viewCard: "کارت",
        viewList: "فهرست",
        actions: "عملیات",
        noCode: "—",
        selectProvince: "انتخاب استان...",
        selectCity: "انتخاب شهر...",
        selectCityDisabled: "ابتدا استان را انتخاب کنید",
        searchProvincePlaceholder: "جستجوی استان...",
        searchCityPlaceholder: "جستجوی شهر...",
        noOptionsFound: "موردی یافت نشد",
        showingRange: (from, to, total) => `نمایش ${from} تا ${to} از ${total} مشتری`,
        itemsPerPage: "تعداد در هر صفحه",
        prevPage: "قبلی",
        nextPage: "بعدی",
    },
    ar: {
        dir: "rtl",
        title: "إدارة العملاء",
        subtitle: "عرض وإدارة قائمة العملاء",
        searchPlaceholder: "بحث بالاسم، المتجر، الجوال أو كود العميل...",
        addNew: "إضافة عميل",
        edit: "تعديل",
        code: "كود العميل",
        fullName: "الاسم الكامل",
        storeName: "اسم المتجر",
        mobile: "رقم الجوال",
        groups: "التصنيفات المنضم إليها",
        province: "المحافظة",
        city: "المدينة",
        address: "العنوان",
        status: "الحالة",
        active: "نشط",
        inactive: "غير نشط",
        filterAll: "الكل",
        save: "حفظ",
        cancel: "إلغاء",
        modalAddTitle: "إضافة عميل جديد",
        modalEditTitle: "تعديل العميل",
        loading: "جارٍ التحميل...",
        saving: "جارٍ الحفظ...",
        empty: "لم يتم تسجيل أي عميل بعد",
        emptyAction: "إضافة أول عميل",
        noSearchResult: "لا توجد نتائج",
        toastFullNameRequired: "أدخل الاسم الكامل",
        toastMobileRequired: "أدخل رقم الجوال",
        toastAddSuccess: "تمت إضافة العميل بنجاح",
        toastAddFail: "خطأ في إضافة العميل",
        toastUpdateSuccess: "تم تعديل العميل بنجاح",
        toastUpdateFail: "خطأ في تعديل العميل",
        toastFetchFail: "خطأ في جلب قائمة العملاء",
        toastLocationsFetchFail: "خطأ في جلب قائمة المحافظات/المدن",
        toastMobileDuplicate: "رقم الجوال هذا مسجل بالفعل لعميل آخر",
        toastStatusSuccess: "تم تغيير حالة العميل",
        toastStatusFail: "خطأ في تغيير حالة العميل",
        toggleStatusTitle: "انقر لتغيير الحالة",
        confirmToggleTitle: "تغيير حالة العميل",
        confirmToggleActivateMessage: (name) => `هل أنت متأكد من تفعيل العميل «${name}»؟`,
        confirmToggleDeactivateMessage: (name) => `هل أنت متأكد من إلغاء تفعيل العميل «${name}»؟`,
        confirmYes: "نعم، تابع",
        itemsCount: (n) => `${n} عميل`,
        codeBadge: (code) => `${code}`,
        autoCodeHint: "يتم تخصيص كود العميل تلقائياً بعد الحفظ",
        viewCard: "بطاقة",
        viewList: "قائمة",
        actions: "إجراءات",
        noCode: "—",
        selectProvince: "اختر المحافظة...",
        selectCity: "اختر المدينة...",
        selectCityDisabled: "اختر المحافظة أولاً",
        searchProvincePlaceholder: "بحث عن محافظة...",
        searchCityPlaceholder: "بحث عن مدينة...",
        noOptionsFound: "لا توجد نتائج",
        showingRange: (from, to, total) => `عرض ${from} إلى ${to} من ${total} عميل`,
        itemsPerPage: "عدد العناصر في الصفحة",
        prevPage: "السابق",
        nextPage: "التالي",
    },
};

/* -------------------------------------------------------------------------- */
/*  آیکن‌ها                                                                    */
/* -------------------------------------------------------------------------- */
function Icon({ name, size = 14, color = "currentColor" }) {
    const paths = {
        plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
        edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
        search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
        close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
        chevronDown: <polyline points="6 9 12 15 18 9" />,
        users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
        store: <><path d="M3 9l1-5h16l1 5" /><path d="M4 9v10a1 1 0 0 0 1 1h4v-6h6v6h4a1 1 0 0 0 1-1V9" /><path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" /></>,
        phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />,
        pin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
        check: <polyline points="20 6 9 17 4 12" />,
        alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
        grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></>,
        list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            {paths[name] ?? null}
        </svg>
    );
}

/* -------------------------------------------------------------------------- */
/*  Toast (بالای صفحه)                                                        */
/* -------------------------------------------------------------------------- */
function Toast({ message, visible, type }) {
    if (!visible) return null;
    return (
        <div style={{
            position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
            background: type === "error" ? "#EF4444" : "#10B981",
            color: "#fff", padding: "10px 20px", borderRadius: 12,
            fontSize: 12, fontWeight: 500, zIndex: 3000,
            display: "flex", alignItems: "center", gap: 8,
            fontFamily: FONT,
            boxShadow: type === "error" ? "0 8px 24px rgba(239,68,68,.3)" : "0 8px 24px rgba(16,185,129,.3)",
            animation: "toastDrop .25s ease both",
        }}>
            <Icon name={type === "error" ? "alert" : "check"} size={13} color="#fff" />
            {message}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  فیلدهای فرم                                                                */
/* -------------------------------------------------------------------------- */
const fieldBaseStyle = {
    width: "100%", height: 40, border: "1.5px solid #E8ECF4",
    borderRadius: 10, padding: "0 12px", fontSize: 12,
    fontFamily: FONT, direction: "rtl",
    outline: "none", color: "#1E293B", boxSizing: "border-box",
    background: "#F8FAFF", transition: "all .15s",
};

function TextField({ value, onChange, placeholder, autoFocus, hasError }) {
    return (
        <input
            autoFocus={autoFocus}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            style={{
                ...fieldBaseStyle,
                ...(hasError ? { border: "1.5px solid #EF4444", background: "#FEF2F2" } : {}),
            }}
            onFocus={(e) => { e.target.style.borderColor = hasError ? "#EF4444" : "#6C3DFF"; e.target.style.background = "#fff"; e.target.style.boxShadow = hasError ? "0 0 0 4px rgba(239,68,68,.08)" : "0 0 0 4px rgba(108,61,255,.08)"; }}
            onBlur={(e) => { e.target.style.borderColor = hasError ? "#EF4444" : "#E8ECF4"; e.target.style.background = hasError ? "#FEF2F2" : "#F8FAFF"; e.target.style.boxShadow = "none"; }}
        />
    );
}

/* -------------------------------------------------------------------------- */
/*  دراپ‌داون قابل‌جستجو (استفاده برای استان / شهر)                             */
/* -------------------------------------------------------------------------- */
function SearchableSelect({ value, onChange, options, placeholder, searchPlaceholder, emptyText, disabled, hasError }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const selected = options.find((o) => o.value === value);
    const filtered = search.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
        : options;

    return (
        <div ref={wrapRef} style={{ position: "relative" }}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                style={{
                    ...fieldBaseStyle,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                    ...(hasError ? { border: "1.5px solid #EF4444", background: "#FEF2F2" } : {}),
                }}
            >
                <span style={{
                    color: selected ? "#1E293B" : "#94A3B8", fontWeight: selected ? 600 : 400,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "start",
                }}>
                    {selected ? selected.label : placeholder}
                </span>
                <Icon name="chevronDown" size={13} color="#94A3B8" />
            </button>

            {open && !disabled && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0, left: 0, zIndex: 60,
                    background: "#fff", border: "1.5px solid #E8ECF4", borderRadius: 10,
                    boxShadow: "0 12px 28px rgba(15,23,42,0.14)", maxHeight: 230,
                    display: "flex", flexDirection: "column", overflow: "hidden",
                }}>
                    <div style={{ padding: 8, borderBottom: "1px solid #F1F5F9", flexShrink: 0 }}>
                        <div style={{ position: "relative" }}>
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={searchPlaceholder}
                                style={{ ...fieldBaseStyle, height: 32, fontSize: 11, padding: "0 30px 0 10px" }}
                            />
                            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }}>
                                <Icon name="search" size={12} color="#94A3B8" />
                            </span>
                        </div>
                    </div>
                    <div style={{ overflowY: "auto" }}>
                        {filtered.length === 0 ? (
                            <p style={{ margin: 0, padding: "10px 12px", fontSize: 11, color: "#94A3B8" }}>{emptyText}</p>
                        ) : (
                            filtered.map((opt) => {
                                const isSelected = opt.value === value;
                                return (
                                    <div
                                        key={opt.value}
                                        onClick={() => { onChange(opt.value); setOpen(false); setSearch(""); }}
                                        style={{
                                            padding: "9px 12px", fontSize: 11.5, cursor: "pointer",
                                            background: isSelected ? "#6C3DFF14" : "transparent",
                                            color: isSelected ? "#6C3DFF" : "#1E293B",
                                            fontWeight: isSelected ? 700 : 500,
                                            transition: "background .12s",
                                        }}
                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F8FAFF"; }}
                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                                    >
                                        {opt.label}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نرمال‌سازی داده‌ها (منطبق بر Entity/ViewModel واقعی)                        */
/*  اگر پاسخ سرور PascalCase برگرداند (FullName, Mobile, ...) هم پوشش داده شده  */
/* -------------------------------------------------------------------------- */

// نام گروه‌بندی‌هایی که مشتری عضو آن‌هاست را از پاسخ سرور استخراج می‌کند.
// چند حالت محتمل برای شکل داده پوشش داده شده (آرایه رشته یا آرایه آبجکت).
function normalizeGroupNames(raw) {
    const source =
        raw.groupNames ?? raw.GroupNames ??
        raw.groupingNames ?? raw.GroupingNames ??
        raw.groups ?? raw.Groups ??
        raw.customerGroups ?? raw.CustomerGroups ??
        [];
    if (!Array.isArray(source)) return [];
    return source
        .map((g) =>
            typeof g === "string"
                ? g
                : g?.name ?? g?.Name ?? g?.groupName ?? g?.GroupName ?? g?.title ?? g?.Title ?? ""
        )
        .filter(Boolean);
}

const normalizeCustomer = (raw) => ({
    id: raw.id ?? raw.Id,
    customerCode: raw.customerCode ?? raw.CustomerCode ?? null,
    fullName: raw.fullName ?? raw.FullName ?? "",
    storeName: raw.storeName ?? raw.StoreName ?? "",
    mobile: raw.mobile ?? raw.Mobile ?? "",
    cityId: raw.cityId ?? raw.CityId ?? null,
    cityName: raw.cityName ?? raw.CityName ?? "",
    provinceName: raw.provinceName ?? raw.ProvinceName ?? "",
    address: raw.address ?? raw.Address ?? "",
    isActive: raw.isActive ?? raw.IsActive ?? true,
    groupNames: normalizeGroupNames(raw),
});

const normalizeProvince = (raw) => ({
    id: raw.id ?? raw.Id,
    nameFa: raw.nameFa ?? raw.NameFa ?? "",
    nameAr: raw.nameAr ?? raw.NameAr ?? "",
});

const normalizeCity = (raw) => ({
    id: raw.id ?? raw.Id,
    provinceId: raw.provinceId ?? raw.ProvinceId ?? null,
    nameFa: raw.nameFa ?? raw.NameFa ?? "",
    nameAr: raw.nameAr ?? raw.NameAr ?? "",
});

function getInitials(name) {
    const trimmed = (name || "").trim();
    return trimmed ? trimmed.charAt(0) : "؟";
}

/* -------------------------------------------------------------------------- */
/*  بج وضعیت                                                                   */
/* -------------------------------------------------------------------------- */
function StatusBadge({ active, t, onClick, loading }) {
    const clickable = typeof onClick === "function";
    return (
        <button
            type="button"
            onClick={clickable ? onClick : undefined}
            disabled={!clickable || loading}
            title={clickable ? t.toggleStatusTitle : undefined}
            style={{
                justifySelf: "start", // <-- اضافه شد
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 9.5, fontWeight: 700, padding: "3px 9px", borderRadius: 99,
                background: active ? "#10B98114" : "#94A3B814",
                color: active ? "#10B981" : "#94A3B8",
                border: "none", fontFamily: FONT,
                cursor: clickable ? (loading ? "wait" : "pointer") : "default",
                opacity: loading ? 0.65 : 1,
                transition: "opacity .15s",
                whiteSpace: "nowrap",
            }}
        >
            {loading ? (
                <span style={{ width: 8, height: 8, borderRadius: "50%", border: "1.5px solid rgba(0,0,0,.15)", borderTopColor: active ? "#10B981" : "#94A3B8", animation: "spin .7s linear infinite" }} />
            ) : (
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#10B981" : "#94A3B8" }} />
            )}
            {active ? t.active : t.inactive}
        </button>
    );
}

/* -------------------------------------------------------------------------- */
/*  کارت مشتری                                                                 */
/* -------------------------------------------------------------------------- */
function CustomerCard({ customer, onEdit, onToggleStatus, isToggling, t }) {
    const location = [customer.cityName, customer.provinceName].filter(Boolean).join("، ");
    return (
        <div
            className="cust-card"
            style={{
                background: "#fff", borderRadius: 16, border: "1px solid #EEF2FF",
                padding: 16, display: "flex", flexDirection: "column", gap: 12,
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)", transition: "all .18s",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: "linear-gradient(135deg, #6C3DFF1A, #9B59FF33)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 15, fontWeight: 700, color: "#6C3DFF",
                }}>
                    {getInitials(customer.fullName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {customer.fullName}
                        </p>
                        {customer.customerCode != null && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#94A3B8" }}>
                                {t.codeBadge(customer.customerCode)}
                            </span>
                        )}
                    </div>
                    {customer.storeName && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                            <Icon name="store" size={10} color="#9B59FF" />
                            <span style={{ fontSize: 10.5, color: "#9B59FF", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {customer.storeName}
                            </span>
                        </div>
                    )}
                    <div style={{ marginTop: 5 }}>
                        <StatusBadge
                            active={customer.isActive}
                            t={t}
                            onClick={onToggleStatus ? () => onToggleStatus(customer) : undefined}
                            loading={isToggling}
                        />
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {customer.mobile && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748B" }}>
                        <Icon name="phone" size={12} color="#94A3B8" />
                        {customer.mobile}
                    </div>
                )}
                {(location || customer.address) && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11, color: "#64748B" }}>
                        <Icon name="pin" size={12} color="#94A3B8" />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {[location, customer.address].filter(Boolean).join(" - ")}
                        </span>
                    </div>
                )}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                    onClick={() => onEdit(customer)}
                    style={{
                        flex: 1, height: 34, borderRadius: 9, border: "1px solid #E8ECF4", background: "#fff",
                        color: "#1E293B", fontSize: 11, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}
                >
                    <Icon name="edit" size={12} color="#6C3DFF" />
                    {t.edit}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نمای فهرستی (جدول) مشتریان                                                 */
/* -------------------------------------------------------------------------- */
const LIST_GRID_COLUMNS = "80px 1fr 0.85fr 0.85fr 1fr 1.3fr 90px 60px";
const LIST_MAX_BODY_HEIGHT = 560; // فقط بدنه جدول اسکرول می‌خورد، نه کل صفحه

function CustomerListView({ customers, onEdit, onToggleStatus, togglingIds, t }) {
    const headers = [t.code, t.fullName, t.storeName, t.mobile, t.groups, t.address, t.status, t.actions];
    return (
        <div style={{
            background: "#fff", borderRadius: 16, border: "1px solid #EEF2FF",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            overflowX: "auto", // اسکرول افقی به‌جای فشرده‌شدن
        }}>
            <div style={{ minWidth: 780 }}> {/* عرض حداقلی ثابت برای جدول */}
                <div style={{
                    display: "grid", gridTemplateColumns: LIST_GRID_COLUMNS,
                    padding: "12px 16px", background: "#F8FAFF", borderBottom: "1px solid #F1F5F9",
                    position: "sticky", top: 0, zIndex: 1, // هدر همیشه ثابت بماند
                }}>
                    {headers.map((h, i) => (
                        <span key={i} style={{ fontSize: 10.5, fontWeight: 700, color: "#94A3B8", whiteSpace: "nowrap" }}>{h}</span>
                    ))}
                </div>

                <div style={{ maxHeight: LIST_MAX_BODY_HEIGHT, overflowY: "auto" }}>
                    {customers.map((customer, i) => {
                        const fullAddress = [customer.provinceName, customer.cityName, customer.address]
                            .filter(Boolean)
                            .join("، ");
                        return (
                            <div
                                key={customer.id}
                                className="cust-row"
                                style={{
                                    display: "grid", gridTemplateColumns: LIST_GRID_COLUMNS,
                                    padding: "12px 16px", alignItems: "center",
                                    borderBottom: "1px solid #F5F7FF", transition: "background .15s",
                                    background: i % 2 === 1 ? "#EEF1F7" : "#fff",
                                }}
                            >
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8" }}>
                                    {customer.customerCode != null ? t.codeBadge(customer.customerCode) : t.noCode}
                                </span>

                                <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {customer.fullName}
                                </span>

                                <span style={{ fontSize: 11.5, color: "#9B59FF", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {customer.storeName || "—"}
                                </span>

                                <span style={{ fontSize: 11.5, color: "#64748B", whiteSpace: "nowrap" }}>
                                    {customer.mobile || "—"}
                                </span>

                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                    {customer.groupNames && customer.groupNames.length > 0 ? (
                                        customer.groupNames.map((g, gi) => (
                                            <span
                                                key={gi}
                                                style={{
                                                    fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                                                    background: "#6C3DFF14", color: "#6C3DFF", whiteSpace: "nowrap",
                                                }}
                                            >
                                                {g}
                                            </span>
                                        ))
                                    ) : (
                                        <span style={{ fontSize: 11, color: "#CBD5E1" }}>—</span>
                                    )}
                                </div>

                                <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {fullAddress || "—"}
                                </span>

                                <StatusBadge
                                    active={customer.isActive}
                                    t={t}
                                    onClick={onToggleStatus ? () => onToggleStatus(customer) : undefined}
                                    loading={togglingIds.has(customer.id)}
                                />

                                <button
                                    onClick={() => onEdit(customer)}
                                    title={t.edit}
                                    style={{
                                        justifySelf: "start", // <-- اضافه شد
                                        width: 30, height: 30, borderRadius: 8, border: "1px solid #E8ECF4",
                                        background: "#fff", cursor: "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >
                                    <Icon name="edit" size={13} color="#6C3DFF" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  تاگل نمای کارت / فهرست                                                     */
/* -------------------------------------------------------------------------- */
function ViewToggle({ mode, onChange, t }) {
    const options = [
        { key: "list", label: t.viewList, icon: "list" },
        { key: "card", label: t.viewCard, icon: "grid" },
    ];
    return (
        <div style={{ display: "flex", gap: 2, background: "#F1F5F9", borderRadius: 10, padding: 3 }}>
            {options.map((opt) => {
                const active = mode === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => onChange(opt.key)}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            height: 32, padding: "0 12px", borderRadius: 8, border: "none",
                            fontFamily: FONT, fontSize: 11, fontWeight: 600, cursor: "pointer",
                            background: active ? "#fff" : "transparent",
                            color: active ? "#6C3DFF" : "#94A3B8",
                            boxShadow: active ? "0 1px 4px rgba(15,23,42,0.08)" : "none",
                            transition: "all .15s",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Icon name={opt.icon} size={13} color={active ? "#6C3DFF" : "#94A3B8"} />
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  پیجینیشن                                                                   */
/* -------------------------------------------------------------------------- */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange, t }) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, total);

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (page > 3) pages.push("…");
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
            pages.push(i);
        }
        if (page < totalPages - 2) pages.push("…");
        pages.push(totalPages);
        return pages;
    };

    return (
        <div
            style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 12, padding: "14px 18px", marginTop: 14,
                background: "#fff", borderRadius: 16, border: "1px solid #EEF2FF",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)", fontFamily: FONT,
            }}
        >
            {/* راست: متن نمایش + انتخاب تعداد در صفحه */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>
                    {t.showingRange(startItem, endItem, total)}
                </span>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>{t.itemsPerPage}</span>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        style={{
                            height: 32, padding: "0 10px", borderRadius: 9, border: "1.5px solid #E8ECF4",
                            background: "#F8FAFF", color: "#1E293B", fontSize: 11.5, fontWeight: 600,
                            fontFamily: FONT, cursor: "pointer", outline: "none",
                        }}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* چپ: قبلی / شماره صفحات / بعدی */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <button
                    type="button"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    style={{
                        height: 32, padding: "0 14px", borderRadius: 9,
                        border: "1.5px solid #E8ECF4", background: "#fff",
                        color: page <= 1 ? "#CBD5E1" : "#475569", fontSize: 11, fontWeight: 600,
                        fontFamily: FONT, cursor: page <= 1 ? "not-allowed" : "pointer",
                        transition: "all .15s",
                    }}
                >
                    {t.prevPage}
                </button>

                {getPageNumbers().map((p, i) =>
                    p === "…" ? (
                        <span key={`dots-${i}`} style={{ fontSize: 11, color: "#94A3B8", padding: "0 2px" }}>
                            …
                        </span>
                    ) : (
                        <button
                            type="button"
                            key={p}
                            onClick={() => onPageChange(p)}
                            style={{
                                width: 32, height: 32, borderRadius: 9, border: "none",
                                background: p === page ? "#6C3DFF" : "transparent",
                                color: p === page ? "#fff" : "#475569",
                                fontSize: 11.5, fontWeight: 700, fontFamily: FONT,
                                cursor: "pointer", transition: "all .15s",
                            }}
                        >
                            {p}
                        </button>
                    )
                )}

                <button
                    type="button"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    style={{
                        height: 32, padding: "0 14px", borderRadius: 9,
                        border: "1.5px solid #E8ECF4", background: "#fff",
                        color: page >= totalPages ? "#CBD5E1" : "#475569", fontSize: 11, fontWeight: 600,
                        fontFamily: FONT, cursor: page >= totalPages ? "not-allowed" : "pointer",
                        transition: "all .15s",
                    }}
                >
                    {t.nextPage}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  کامپوننت اصلی                                                              */
/* -------------------------------------------------------------------------- */
export default function CustomerManagement() {
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;

    const [customers, setCustomers] = useState([]);
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
    const [viewMode, setViewMode] = useState("list"); // card | list — پیش‌فرض: فهرست
    const [modal, setModal] = useState(null); // {mode, loadingData, data:{id, customerCode, fullName, storeName, mobile, provinceId, cityId, address, isActive} }
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [togglingIds, setTogglingIds] = useState(() => new Set());
    const [confirmToggle, setConfirmToggle] = useState(null); // مشتری‌ای که منتظر تایید تغییر وضعیت است
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
    };

    const fetchCustomers = async () => {
        try {
            const res = await GetAllCustomer();
            const raw = res.data?.data ?? res.data ?? [];
            setCustomers(Array.isArray(raw) ? raw.map(normalizeCustomer) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const fetchProvinces = async () => {
        try {
            const res = await GetAllProvince();
            const raw = res.data?.data ?? res.data ?? [];
            setProvinces(Array.isArray(raw) ? raw.map(normalizeProvince) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastLocationsFetchFail, "error");
        }
    };

    const fetchCities = async () => {
        try {
            const res = await GetAllCity();
            const raw = res.data?.data ?? res.data ?? [];
            setCities(Array.isArray(raw) ? raw.map(normalizeCity) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastLocationsFetchFail, "error");
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchCustomers(), fetchProvinces(), fetchCities()]);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        let list = customers;
        if (statusFilter !== "all") {
            const wantActive = statusFilter === "active";
            list = list.filter((c) => !!c.isActive === wantActive);
        }
        if (searchTerm.trim()) {
            const lower = searchTerm.trim().toLowerCase();
            list = list.filter(
                (c) =>
                    (c.fullName || "").toLowerCase().includes(lower) ||
                    (c.storeName || "").toLowerCase().includes(lower) ||
                    (c.mobile || "").includes(searchTerm.trim()) ||
                    String(c.customerCode ?? "").includes(searchTerm.trim())
            );
        }
        return list;
    }, [customers, statusFilter, searchTerm]);

    // بازگشت به صفحه اول هنگام تغییر فیلتر، جستجو یا تعداد آیتم در هر صفحه
    useEffect(() => {
        setPage(1);
    }, [statusFilter, searchTerm, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

    // اگر صفحه فعلی از تعداد کل صفحات بیشتر شد (مثلا بعد از حذف)، به آخرین صفحه معتبر برگرد
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginated = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    const counts = useMemo(() => ({
        all: customers.length,
        active: customers.filter((c) => c.isActive).length,
        inactive: customers.filter((c) => !c.isActive).length,
    }), [customers]);

    // گزینه‌های دراپ‌داون استان‌ها؛ برچسب بر اساس زبان جاری (NameFa / NameAr)
    const provinceOptions = useMemo(
        () => provinces.map((p) => ({
            value: p.id,
            label: (lang === "ar" ? p.nameAr : p.nameFa) || p.nameFa || p.nameAr,
        })),
        [provinces, lang]
    );

    // گزینه‌های دراپ‌داون شهرها، فیلترشده بر اساس استان انتخاب‌شده در فرم
    const cityOptions = useMemo(() => {
        const provinceId = modal?.data?.provinceId ?? null;
        if (!provinceId) return [];
        return cities
            .filter((c) => c.provinceId === provinceId)
            .map((c) => ({
                value: c.id,
                label: (lang === "ar" ? c.nameAr : c.nameFa) || c.nameFa || c.nameAr,
            }));
    }, [cities, modal?.data?.provinceId, lang]);

    const emptyFormData = {
        id: null, customerCode: null, fullName: "", storeName: "", mobile: "",
        provinceId: null, cityId: null, address: "", isActive: true,
    };

    const openAddModal = () => {
        setFieldErrors({});
        setModal({ mode: "add", loadingData: false, data: { ...emptyFormData } });
    };

    // ✅ اصلاح‌شده
    const openEditModal = async (customer) => {
        setFieldErrors({});
        const initialProvinceId = cities.find((c) => c.id === customer.cityId)?.provinceId ?? null;
        setModal({ mode: "edit", loadingData: true, data: { ...customer, provinceId: initialProvinceId } });
        try {
            const res = await GetCustomerById(customer.id);
            const raw = res.data?.data ?? res.data;
            if (raw) {
                const fresh = normalizeCustomer(raw);
                const provinceId = cities.find((c) => c.id === fresh.cityId)?.provinceId ?? null;
                setModal({ mode: "edit", loadingData: false, data: { ...fresh, id: customer.id, provinceId } });
            } else {
                setModal((m) => (m ? { ...m, loadingData: false } : m));
            }
        } catch (err) {
            setModal(null); // مودال رو کلاً می‌بندیم چون داده‌ای برای نمایش نداریم
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const closeModal = () => { if (!saving) setModal(null); };

    const extractServerErrorMessage = (err) => {
        const data = err?.response?.data;
        if (typeof data === "string" && data.trim()) return data.trim();
        if (data && typeof data === "object") {
            return data.message || data.title || data.error || null;
        }
        return null;
    };

    const isDuplicateMobileMessage = (msg) =>
        !!msg && (msg.includes("تکراری") || msg.includes("قبلاً ثبت شده") || msg.includes("قبلا ثبت شده") || msg.includes("مسجل بالفعل"));

    const handleSubmit = async () => {
        if (!modal) return;
        const fullName = modal.data.fullName?.trim();
        const mobile = modal.data.mobile?.trim();
        if (!fullName) { showToast(t.toastFullNameRequired, "error"); return; }
        if (!mobile) { showToast(t.toastMobileRequired, "error"); return; }
        setFieldErrors({});

        const payload = {
            ...(modal.mode === "edit" ? { id: modal.data.id, customerCode: modal.data.customerCode } : {}),
            fullName,
            storeName: modal.data.storeName?.trim() || null,
            mobile,
            cityId: modal.data.cityId || null,
            address: modal.data.address?.trim() || null,
            // مشتری جدید همیشه فعال ایجاد می‌شود؛ در ویرایش مقدار انتخابی کاربر ارسال می‌شود
            isActive: modal.mode === "edit" ? !!modal.data.isActive : true,
        };

        setSaving(true);
        try {
            if (modal.mode === "add") {
                await AddCustomer(payload);
                showToast(t.toastAddSuccess);
            } else {
                await UpdateCustomer(payload);
                showToast(t.toastUpdateSuccess);
            }
            setModal(null);
            await fetchCustomers();
        } catch (err) {
            console.error("خطای ذخیره مشتری:", err, err?.response?.data);
            const serverMsg = extractServerErrorMessage(err);
            if (isDuplicateMobileMessage(serverMsg)) {
                setFieldErrors({ mobile: t.toastMobileDuplicate });
                showToast(t.toastMobileDuplicate, "error");
            } else {
                showToast(serverMsg || (modal.mode === "add" ? t.toastAddFail : t.toastUpdateFail), "error");
            }
        } finally {
            setSaving(false);
        }
    };

    // با کلیک روی بج وضعیت، فقط دیالوگ تایید باز می‌شود؛ تغییر واقعی بعد از تایید کاربر انجام می‌گیرد
    const requestToggleStatus = (customer) => {
        if (togglingIds.has(customer.id)) return;
        setConfirmToggle(customer);
    };

    const closeConfirmToggle = () => setConfirmToggle(null);

    const confirmToggleStatus = async () => {
        if (!confirmToggle) return;
        const customer = confirmToggle;
        setConfirmToggle(null);
        await performToggleStatus(customer);
    };

    const performToggleStatus = async (customer) => {
        if (togglingIds.has(customer.id)) return;
        const nextActive = !customer.isActive;
        setTogglingIds((prev) => new Set(prev).add(customer.id));
        try {
            await UpdateCustomer({
                id: customer.id,
                customerCode: customer.customerCode,
                fullName: customer.fullName,
                storeName: customer.storeName || null,
                mobile: customer.mobile,
                cityId: customer.cityId || null,
                address: customer.address || null,
                isActive: nextActive,
            });
            setCustomers((prev) => prev.map((c) => (c.id === customer.id ? { ...c, isActive: nextActive } : c)));
            showToast(t.toastStatusSuccess);
        } catch (err) {
            console.error("خطای تغییر وضعیت مشتری:", err, err?.response?.data);
            const serverMsg = extractServerErrorMessage(err);
            if (isDuplicateMobileMessage(serverMsg)) {
                showToast(t.toastMobileDuplicate, "error");
            } else {
                showToast(serverMsg || t.toastStatusFail, "error");
            }
        } finally {
            setTogglingIds((prev) => {
                const next = new Set(prev);
                next.delete(customer.id);
                return next;
            });
        }
    };

    const filterTabs = [
        { key: "all", label: t.filterAll, color: "#6C3DFF" },
        { key: "active", label: t.active, color: "#10B981" },
        { key: "inactive", label: t.inactive, color: "#94A3B8" },
    ];

    return (
        <div style={{ fontFamily: FONT, direction: t.dir, minHeight: "100vh", background: "#F5F6FA" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
                @keyframes toastDrop { from { opacity:0; transform:translate(-50%,-12px); } to { opacity:1; transform:translate(-50%,0); } }
                .cust-card:hover { box-shadow: 0 8px 24px rgba(108,61,255,0.10); transform: translateY(-2px); border-color: #E4DBFF; }
                .cust-row:hover { background: #FAFAFF !important; }
            `}</style>

            {/* هدر */}
            <div style={{
                background: "linear-gradient(135deg, #6C3DFF 0%, #9B59FF 100%)",
                padding: "26px 28px 84px", position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                <div style={{ position: "absolute", bottom: -40, right: 80, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "relative", zIndex: 1 }}>
                    <div>
                        <h1 style={{ fontSize: 19, fontWeight: 700, color: "#fff", margin: 0 }}>{t.title}</h1>
                        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", margin: "4px 0 0" }}>{t.subtitle}</p>
                    </div>

                    <button
                        onClick={openAddModal}
                        style={{
                            height: 40, background: "#fff", border: "none", borderRadius: 12, color: "#6C3DFF",
                            fontSize: 11.5, fontFamily: FONT, fontWeight: 700, cursor: "pointer",
                            padding: "0 16px", display: "flex", alignItems: "center", gap: 6,
                            boxShadow: "0 10px 24px rgba(15,23,42,0.18)", whiteSpace: "nowrap", flexShrink: 0,
                        }}
                    >
                        <Icon name="plus" size={13} color="#6C3DFF" />
                        {t.addNew}
                    </button>
                </div>
            </div>

            {/* باکس جستجو + فیلتر + فهرست/کارت */}
            <div style={{ padding: "0 28px", marginTop: -56, animation: "fadeUp .4s ease both", position: "relative", zIndex: 2 }}>
                <div style={{
                    background: "#fff", borderRadius: 18,
                    border: "1px solid #EEF2FF", boxShadow: "0 8px 28px rgba(108,61,255,0.10)",
                    overflow: "hidden",
                }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
                        {filterTabs.map((tab) => {
                            const active = statusFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setStatusFilter(tab.key)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6,
                                        padding: "7px 12px", borderRadius: 9, border: "none",
                                        cursor: "pointer", fontFamily: FONT,
                                        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                                        transition: "all .18s",
                                        background: active ? `linear-gradient(135deg, ${tab.color}, ${tab.color}CC)` : "transparent",
                                        color: active ? "#fff" : "#64748B",
                                        boxShadow: active ? `0 4px 10px ${tab.color}40` : "none",
                                    }}
                                >
                                    {tab.label}
                                    <span style={{
                                        fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 99,
                                        background: active ? "rgba(255,255,255,0.25)" : "#F1F5F9",
                                        color: active ? "#fff" : "#94A3B8",
                                    }}>
                                        {counts[tab.key] ?? 0}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", flexWrap: "wrap" }}>
                        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t.searchPlaceholder}
                                style={{ ...fieldBaseStyle, padding: "0 36px 0 12px" }}
                            />
                            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                                <Icon name="search" size={13} color="#94A3B8" />
                            </span>
                        </div>

                        <span style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap" }}>{t.itemsCount(filtered.length)}</span>

                        <ViewToggle mode={viewMode} onChange={setViewMode} t={t} />
                    </div>
                </div>
            </div>

            {/* گرید / جدول مشتریان */}
            <div style={{ padding: "18px 28px 40px", animation: "fadeUp .5s .1s ease both" }}>
                {loading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 0" }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite", marginBottom: 12 }} />
                        <p style={{ color: "#94A3B8", fontSize: 12 }}>{t.loading}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{
                        background: "#fff", borderRadius: 18, border: "1px solid #EEF2FF",
                        display: "flex", flexDirection: "column", alignItems: "center", padding: "46px 0", gap: 12,
                    }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: "#6C3DFF14" }}>
                            <Icon name="users" size={20} color="#6C3DFF" />
                        </div>
                        <p style={{ color: "#94A3B8", fontSize: 12, margin: 0 }}>
                            {searchTerm.trim() || statusFilter !== "all" ? t.noSearchResult : t.empty}
                        </p>
                        {!searchTerm.trim() && statusFilter === "all" && (
                            <button
                                onClick={openAddModal}
                                style={{
                                    background: "#6C3DFF14", color: "#6C3DFF", border: "none", borderRadius: 10,
                                    padding: "7px 16px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT,
                                }}
                            >
                                {t.emptyAction}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {viewMode === "card" ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
                                {paginated.map((customer) => (
                                    <CustomerCard
                                        key={customer.id}
                                        customer={customer}
                                        onEdit={openEditModal}
                                        onToggleStatus={requestToggleStatus}
                                        isToggling={togglingIds.has(customer.id)}
                                        t={t}
                                    />
                                ))}
                            </div>
                        ) : (
                            <CustomerListView
                                customers={paginated}
                                onEdit={openEditModal}
                                onToggleStatus={requestToggleStatus}
                                togglingIds={togglingIds}
                                t={t}
                            />
                        )}

                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            total={filtered.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            t={t}
                        />
                    </>
                )}
            </div>

            {/* مودال افزودن / ویرایش مشتری */}
            {modal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 16 }}>
                    <div style={{ background: "#fff", borderRadius: 18, width: "min(420px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(15,23,42,.25)" }}>
                        <div style={{ padding: "16px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <span style={{ width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#6C3DFF14" }}>
                                    <Icon name="users" size={12} color="#6C3DFF" />
                                </span>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                                    {modal.mode === "add" ? t.modalAddTitle : t.modalEditTitle}
                                </p>
                            </div>
                            <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                                <Icon name="close" size={16} color="#94A3B8" />
                            </button>
                        </div>

                        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
                            {modal.loadingData ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "18px 0" }}>
                                    <div style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite" }} />
                                </div>
                            ) : (
                                <>
                                    {modal.mode === "edit" && (
                                        <div>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.status}</label>
                                            <div style={{ display: "flex", gap: 8 }}>
                                                {[{ v: true, l: t.active, c: "#10B981" }, { v: false, l: t.inactive, c: "#94A3B8" }].map((opt) => {
                                                    const active = !!modal.data.isActive === opt.v;
                                                    return (
                                                        <button
                                                            key={String(opt.v)}
                                                            type="button"
                                                            onClick={() => setModal((m) => ({ ...m, data: { ...m.data, isActive: opt.v } }))}
                                                            style={{
                                                                flex: 1, height: 38, borderRadius: 10,
                                                                border: active ? `1.5px solid ${opt.c}` : "1.5px solid #E8ECF4",
                                                                background: active ? `${opt.c}14` : "#F8FAFF",
                                                                color: active ? opt.c : "#94A3B8",
                                                                fontSize: 11.5, fontWeight: 700, fontFamily: FONT, cursor: "pointer",
                                                            }}
                                                        >
                                                            {opt.l}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {modal.mode === "add" && (
                                        <p style={{ margin: 0, fontSize: 10, color: "#94A3B8" }}>{t.autoCodeHint}</p>
                                    )}

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.fullName}</label>
                                        <TextField
                                            autoFocus
                                            value={modal.data.fullName}
                                            onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, fullName: e.target.value } }))}
                                            placeholder={t.fullName}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.storeName}</label>
                                        <TextField
                                            value={modal.data.storeName}
                                            onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, storeName: e.target.value } }))}
                                            placeholder={t.storeName}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.mobile}</label>
                                        <TextField
                                            value={modal.data.mobile}
                                            onChange={(e) => {
                                                setModal((m) => ({ ...m, data: { ...m.data, mobile: e.target.value } }));
                                                if (fieldErrors.mobile) setFieldErrors((fe) => ({ ...fe, mobile: null }));
                                            }}
                                            placeholder={t.mobile}
                                            hasError={!!fieldErrors.mobile}
                                        />
                                        {fieldErrors.mobile && (
                                            <p style={{ margin: "6px 0 0", fontSize: 10, color: "#EF4444" }}>{fieldErrors.mobile}</p>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: 10 }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.province}</label>
                                            <SearchableSelect
                                                value={modal.data.provinceId}
                                                onChange={(id) => setModal((m) => ({ ...m, data: { ...m.data, provinceId: id, cityId: null } }))}
                                                options={provinceOptions}
                                                placeholder={t.selectProvince}
                                                searchPlaceholder={t.searchProvincePlaceholder}
                                                emptyText={t.noOptionsFound}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.city}</label>
                                            <SearchableSelect
                                                value={modal.data.cityId}
                                                onChange={(id) => setModal((m) => ({ ...m, data: { ...m.data, cityId: id } }))}
                                                options={cityOptions}
                                                placeholder={modal.data.provinceId ? t.selectCity : t.selectCityDisabled}
                                                searchPlaceholder={t.searchCityPlaceholder}
                                                emptyText={t.noOptionsFound}
                                                disabled={!modal.data.provinceId}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.address}</label>
                                        <TextField
                                            value={modal.data.address}
                                            onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, address: e.target.value } }))}
                                            placeholder={t.address}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ padding: "14px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "#fff" }}>
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving || modal.loadingData}
                                style={{
                                    height: 38, padding: "0 20px", borderRadius: 10, border: "none",
                                    background: saving ? "#A78BFA" : "#6C3DFF", color: "#fff", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: saving ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                }}
                            >
                                {saving && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                {saving ? t.saving : t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmToggle && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1600, padding: 16 }}>
                    <div style={{ background: "#fff", borderRadius: 18, width: "min(360px, 100%)", boxShadow: "0 20px 60px rgba(15,23,42,.25)", padding: 22 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1E293B" }}>{t.confirmToggleTitle}</h3>
                        <p style={{ margin: "10px 0 0", fontSize: 12, color: "#64748B", lineHeight: 1.7 }}>
                            {confirmToggle.isActive
                                ? t.confirmToggleDeactivateMessage(confirmToggle.fullName)
                                : t.confirmToggleActivateMessage(confirmToggle.fullName)}
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                            <button
                                onClick={closeConfirmToggle}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={confirmToggleStatus}
                                style={{
                                    height: 38, padding: "0 20px", borderRadius: 10, border: "none",
                                    background: confirmToggle.isActive ? "#EF4444" : "#10B981", color: "#fff",
                                    fontSize: 11.5, fontWeight: 700, fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.confirmYes}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        </div>
    );
}